import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "../lib/api";
import { loadCloudinaryConfig } from "../lib/cloudinary";

/**
 * Single source of truth for ALL storefront-wide settings:
 *   - free_delivery_threshold (₹)
 *   - razorpay_key_id, whatsapp_number, announcement_banner_text
 *   - min_order_amount (₹)
 *   - marquee_items, hero_slides, hero_videos (null sentinels supported)
 *   - category_images (per-category image overrides)
 *
 * iter77: the flat `delivery_charge` fallback has been REMOVED. Shiprocket
 * is now the sole source of shipping cost. Any cart/checkout surface must
 * call POST /api/shipping/quote for a live rate.
 *
 * Every storefront surface MUST read from this hook so a single admin save
 * propagates everywhere instantly.
 */
const DEFAULTS = {
  free_delivery_threshold: 999,
  // iter69 — flat handling fee (₹6 default) added to every order to
  // recover Shiprocket's WhatsApp / SMS notification charge.  Admin
  // can override via settings; the storefront pulls the live value
  // from /api/settings.
  handling_fee_amount: 6,
  razorpay_key_id: "",
  whatsapp_number: "",
  contact_number: "",
  // iter71 — editable customer-facing email displayed in Footer,
  // Homepage "Get in touch" section, Contact page and policies.
  contact_email: "sridurgafoodssupport@gmail.com",
  // iter72 — editable business hours line shown subtly below the
  // contact details in the Footer + Contact page.
  business_hours: "Mon – Sat | 10:00 AM – 6:00 PM",
  announcement_banner_text: "",
  min_order_amount: 200,
  marquee_items: null,    // null = use bundled defaults on the storefront
  hero_slides: null,      // null = use bundled defaults
  hero_videos: null,      // null = use bundled defaults
  category_images: null,  // null = use bundled defaults per category
  product_card_slideshow_enabled: true,
  product_card_transition_type: "scale_kenburns",
  free_delivery_enabled: false,
  order_cancellation_window_enabled: false,
  // Session M / iter110 — SAFE DEFAULTS for business-critical toggles.
  // Admin has explicitly disabled COD in production.  If the /api/settings
  // call fails (poor network, service unreachable), the storefront MUST
  // NOT fall back to "COD enabled" — that would surface a payment method
  // the business has deliberately turned off.  The server is still the
  // source of truth; this is a defensive floor for the degraded state.
  cod_enabled: false,
  customer_cancellation_enabled: true,
  bestseller_product_ids: [],
  homepage_categories: [],
};

const SettingsContext = createContext({
  settings: DEFAULTS,
  settingsLoaded: false,
  freeDeliveryThreshold: DEFAULTS.free_delivery_threshold,
  formatThreshold: () => `₹${DEFAULTS.free_delivery_threshold}`,
  amountToFreeDelivery: () => DEFAULTS.free_delivery_threshold,
  refresh: async () => {},
});

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  // iter51: track whether /api/settings has resolved at least once so
  // the storefront can avoid rendering the bundled-default Hero carousel
  // (1:1 placeholders) before the admin's saved 5:7 images arrive.
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      // Session M / iter110 — settings are business-critical (COD toggle,
      // min order amount, marquee, charges).  A stale cached response
      // can expose the customer to a disabled payment method or the
      // wrong marquee.  Bypass every HTTP layer cache for this call.
      const { data } = await api.get("/settings", {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
        },
        params: { _t: Date.now() },
      });
      setSettings({
        free_delivery_threshold: Number(data?.free_delivery_threshold ?? DEFAULTS.free_delivery_threshold),
        handling_fee_amount: Number(data?.handling_fee_amount ?? DEFAULTS.handling_fee_amount),
        razorpay_key_id: data?.razorpay_key_id || "",
        whatsapp_number: data?.whatsapp_number || "",
        contact_number: data?.contact_number || "",
        contact_email: data?.contact_email || DEFAULTS.contact_email,
        business_hours: data?.business_hours || DEFAULTS.business_hours,
        announcement_banner_text: data?.announcement_banner_text || "",
        min_order_amount: Number(data?.min_order_amount ?? DEFAULTS.min_order_amount),
        // Null sentinels are preserved — empty arrays/maps mean "admin
        // explicitly cleared this section, render nothing".
        marquee_items: data?.marquee_items === undefined ? null : data.marquee_items,
        hero_slides: data?.hero_slides === undefined ? null : data.hero_slides,
        hero_videos: data?.hero_videos === undefined ? null : data.hero_videos,
        category_images: data?.category_images === undefined ? null : data.category_images,
        product_card_slideshow_enabled: data?.product_card_slideshow_enabled !== false,
        product_card_transition_type: data?.product_card_transition_type || DEFAULTS.product_card_transition_type,
        free_delivery_enabled: !!data?.free_delivery_enabled,
        order_cancellation_window_enabled: !!data?.order_cancellation_window_enabled,
        // Session M / iter110 — Backend returns booleans.  If the key is
        // ABSENT (older DB, degraded response), fall through to the
        // SAFE floor (COD OFF) — never silently re-enable a payment
        // method the business has disabled.
        cod_enabled: data?.cod_enabled === undefined ? false : !!data.cod_enabled,
        customer_cancellation_enabled:
          data?.customer_cancellation_enabled === undefined
            ? true
            : !!data.customer_cancellation_enabled,
        bestseller_product_ids: Array.isArray(data?.bestseller_product_ids) ? data.bestseller_product_ids : [],
        homepage_categories: Array.isArray(data?.homepage_categories) ? data.homepage_categories : [],
        // Session L / iter109 — dynamic Charges list.  Cart & Checkout
        // render each entry as its own line.  Falls back to the single
        // Handling Fee row when the API doesn't return charges yet.
        charges: Array.isArray(data?.charges) ? data.charges : [],
      });
    } catch {
      // Network failures should not crash the storefront — keep defaults.
    } finally {
      // Flip the loaded flag even on network failure so the Hero can render
      // its bundled fallback after a single retry rather than staying blank
      // forever.
      setSettingsLoaded(true);
    }
  }, []);

  // Initial fetch on mount.
  useEffect(() => {
    refresh();
    // Kick off Cloudinary cloud-name fetch in parallel — used by imageUrl().
    loadCloudinaryConfig();
  }, [refresh]);

  const value = useMemo(() => {
    const threshold = Number(settings.free_delivery_threshold) || DEFAULTS.free_delivery_threshold;
    // Session L / iter109 — derive the ACTIVE charges list.  Handling
    // fee stays as the seeded fallback when the settings response is
    // empty (older storefront + older backend).  `handlingFee` (total)
    // is preserved for backward-compat renderers.
    const activeCharges = (Array.isArray(settings.charges) ? settings.charges : [])
      .filter((c) => c && c.active !== false)
      .map((c) => ({
        id: String(c.id || ""),
        name: String(c.name || "Charge"),
        amount: Number(c.amount) || 0,
      }))
      .filter((c) => c.amount > 0);
    const chargesTotal = activeCharges.reduce((s, c) => s + c.amount, 0);
    const handlingFee = chargesTotal || Number(settings.handling_fee_amount) || DEFAULTS.handling_fee_amount;
    const freeEnabled = !!settings.free_delivery_enabled;
    return {
      settings,
      settingsLoaded,
      freeDeliveryThreshold: threshold,
      handlingFee,
      activeCharges,
      chargesTotal,
      freeDeliveryEnabled: freeEnabled,
      orderCancellationWindowEnabled: !!settings.order_cancellation_window_enabled,
      codEnabled: settings.cod_enabled !== false,
      customerCancellationEnabled: settings.customer_cancellation_enabled !== false,
      formatThreshold: () => `₹${threshold.toFixed(0)}`,
      // iter77 — no fallback delivery charge exists.  Cart / Checkout MUST
      // fetch a live Shiprocket quote via POST /api/shipping/quote; the
      // "Add ₹X more for free delivery" hint remains available.
      amountToFreeDelivery: (subtotal) =>
        freeEnabled ? Math.max(0, threshold - Number(subtotal || 0)) : 0,
      refresh,
    };
  }, [settings, settingsLoaded, refresh]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  return useContext(SettingsContext);
}
